import type { Asset } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { assetFile, FileViewer, isImage, useBlobUrl } from './file-preview';

/**
 * Where a picture in Markdown or a view points (docs/features/assets.md): one of the workspace's assets, by id
 * (`/files/<id>/…`, `/api/assets/<id>/content`, the bare id) or by file name (`cover.png`, or the last part of a path
 * such as `/api/articles/assets/<slug>/cover.png` from a shop that got the picture from Deskworks). Any other address
 * is `external` and never loaded by itself: a picture loads without a click, so it could carry data out.
 */
export type ImageSource = { assetId: string } | { filename: string } | { external: string } | null;

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ASSET_PATH = /^\/(?:files|api\/assets)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;
const IMAGE_NAME = /\.(png|jpe?g|webp|gif|svg)$/i;

export function imageSource(src: unknown): ImageSource {
  const s = typeof src === 'string' ? src.trim() : '';
  if (!s) return null;
  if (ID.test(s)) return { assetId: s };
  let path = s;
  let external: string | null = null;
  if (/^https?:\/\//i.test(s)) {
    try {
      path = new URL(s).pathname;
    } catch {
      return null;
    }
    external = s;
  } else if (/^[a-z][a-z0-9+.-]*:/i.test(s)) {
    return null; // data:, blob:, javascript: …
  }
  // An asset's address on any Deskworks host: only the id is used, the host is never asked.
  const asset = ASSET_PATH.exec(path);
  if (asset) return { assetId: asset[1] };
  if (external) return { external };
  let name = path.split(/[?#]/)[0].split('/').pop() ?? '';
  try {
    name = decodeURIComponent(name);
  } catch {
    // keep it as written
  }
  return IMAGE_NAME.test(name) ? { filename: name } : null;
}

function useImageAsset(source: ImageSource, workspaceId: string) {
  const byId = source && 'assetId' in source ? source.assetId : null;
  const byName = source && 'filename' in source ? source.filename : null;
  return useQuery<Asset | null>({
    queryKey: ['ws', workspaceId, 'image-asset', byId ?? `name:${byName?.toLowerCase()}`],
    queryFn: async () => {
      if (byId) return api.assets.get(byId).catch(() => null);
      const [newest] = await api.assets.list(workspaceId, { filename: byName!, limit: 1 });
      return newest ?? null;
    },
    enabled: !!(byId || (byName && workspaceId)),
    staleTime: 5 * 60_000,
  });
}

/**
 * A picture from the workspace's assets, loaded with the person's login (docs/features/assets.md). A click opens it
 * large. Anything that can't be shown — a foreign address, a file the workspace doesn't have, no picture — is a quiet
 * placeholder with the alt text, never the raw address.
 */
export function AssetImage({
  src,
  alt,
  caption,
  height,
  fit = 'contain',
  className,
}: {
  src: unknown;
  alt?: string;
  caption?: string;
  /** Fixed height in px; without it the picture keeps its proportions (up to 480 px high). */
  height?: number;
  fit?: 'contain' | 'cover';
  className?: string;
}) {
  const workspaceId = useWorkspaceId();
  const source = imageSource(src);
  const asset = useImageAsset(source, workspaceId);
  const file = asset.data ? assetFile(asset.data) : null;
  const image = !!file && isImage(file);
  const blob = useBlobUrl(file ?? { assetId: '', filename: '', mimeType: '', size: 0 }, image);
  const [open, setOpen] = useState(false);
  const label = alt?.trim() || file?.altText || (source && 'filename' in source ? source.filename : '') || t('Picture');
  const box = height ? { height: Math.min(Math.max(height, 40), 1200) } : undefined;

  let body;
  if (image && !blob.failed) {
    body = blob.url ? (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block max-w-full cursor-zoom-in rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        title={label}
      >
        <img
          src={blob.url}
          alt={label}
          style={box}
          className={cn('max-w-full rounded-md border bg-muted/30', height ? (fit === 'cover' ? 'w-full object-cover' : 'object-contain') : 'max-h-[480px] object-contain')}
        />
      </button>
    ) : (
      <span className="block h-40 w-full max-w-md animate-pulse rounded-md border bg-muted/40" style={box} />
    );
  } else if (asset.isLoading) {
    body = <span className="block h-40 w-full max-w-md animate-pulse rounded-md border bg-muted/40" style={box} />;
  } else {
    const href = source && 'external' in source ? source.external : null;
    body = (
      <span className="flex max-w-md items-start gap-2 rounded-md border border-dashed bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        <ImageIcon className="mt-0.5 size-3.5 shrink-0" />
        <span className="min-w-0">
          <span className="block break-words">{label}</span>
          {href ? (
            <a href={href} target="_blank" rel="noreferrer noopener" className="mt-0.5 inline-flex items-center gap-1 text-primary underline underline-offset-2">
              {t('Open picture')} <ExternalLink className="size-3" />
            </a>
          ) : (
            <span className="mt-0.5 block opacity-80">{file ? t('Not a picture Deskworks can show.') : t('Not among this workspace’s files.')}</span>
          )}
        </span>
      </span>
    );
  }

  // Spans only, so a picture may stand inside a paragraph, a list item or a table cell.
  return (
    <span className={cn('flex flex-col gap-1', className)}>
      {body}
      {caption && <span className="text-xs text-muted-foreground">{caption}</span>}
      {open && file && <FileViewer file={file} open={open} onOpenChange={setOpen} />}
    </span>
  );
}
